import React from 'react';
import {
  call, takeLatest, put, select, take,
} from 'redux-saga/effects';
import { authActions } from '@/redux/modules/auth';
import { api } from '@/utils/api';
import { commonActions } from '@/redux/modules/common';
import { locale } from '@/utils/locale';
import { CHANGE_DOMAIN_CODES, DomainsActionTypes } from './domains.types';
import {
  DOMAIN_RENEWAL_URL, DOMAIN_GENERATE_NEW_INVOICE, USER_DOMAINS_URL, DOMAIN_DETAIL_URL, USER_DOMAINS_ELEGIBLE,
  CHANGE_DOMAIN_URL,
  TEMPORARY_DOMAINS_URL,
  GENERATE_TEMP_DOMAIN,
  PRODUCT_REDEEM_FAST_CHECKOUT_URL,
  DOMAIN_REDEEM_AVAILABILITY,
  DOMAIN_PURCHASE_AVAILABILITY,
} from '@/config/api';
import domainsActions from './domains.actions';
import domainsAssembly from './domains.assembly';
import { ORDER_EPP_KEY_MODAL_STEP } from '@/components/Domain/ModalEppKey/ModalEppKey.enum';
import logger from '@/utils/logger';
import { sitesActions } from '../sites';
import { SitesActionTypes } from '../sites/sites.types';
import { isBRServer } from '@/utils/Validators/validation';

function* requestActivatingRenewal(action) {
  const { domainData, callBackFunction } = action.payload;
  const { token } = yield select(state => state.auth);

  const request = {
    data: {
      donotrenew: domainData.donotrenew,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
    url: DOMAIN_RENEWAL_URL(domainData.id),
  };

  try {
    yield call(api.whmcs.put, request);

    yield put(commonActions.notifications.set({
      label: (
        <p>
          {`${locale('domains.notifications.activateRenewal.success.0')} `}
          <strong>{domainData.name}</strong>
          {`${locale('domains.notifications.activateRenewal.success.1')} `}
        </p>
      ),
      type: 'success',
    }));

    callBackFunction();
    yield take([DomainsActionTypes.GET_DOMAINS_SUCCESS, DomainsActionTypes.GET_DOMAINS_FAILURE]);
  } catch (e) {
    yield put(commonActions.notifications.set({
      label: (
        <p>
          {`${locale('domains.notifications.activateRenewal.failure.0')} `}
          <strong>{domainData.name}</strong>
          {`${locale('domains.notifications.activateRenewal.failure.1')} `}
        </p>
      ),
      type: 'error',
    }));
  }
}

function* requestGenerationNewInvoice(action) {
  const { domainData, callBackFunction } = action.payload;
  const { token } = yield select(state => state.auth);

  const request = {
    data: {
      donotrenew: domainData.id,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
    url: DOMAIN_GENERATE_NEW_INVOICE(domainData.id),
  };

  try {
    yield put(domainsActions.domains.generateNewInvoice.loading(true));
    const response = yield call(api.whmcs.put, request);

    if (response.data.data.invoiceid === undefined) {
      throw new Error('Its not returned invoice id');
    }

    callBackFunction(response.data.data.invoiceid);
  } catch (e) {
    yield put(commonActions.notifications.set({
      label: (
        <p>
          {`${locale('domains.notifications.generateNewInvoice.failure.0')} `}
        </p>
      ),
      type: 'error',
    }));
    yield put(domainsActions.domains.generateNewInvoice.failure());
    yield put(domainsActions.domains.generateNewInvoice.loading(false));
  }
}

function* getPropagationStatus() {
  yield put(authActions.tokens.jwt.update());
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: 'api/v2/clientarea/br/domains/propagation',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    const propagationStatus = domainsAssembly.propagationStatus(response);
    yield put(domainsActions.propagationStatus.success());
    yield put(domainsActions.propagationStatus.set(propagationStatus));
  } catch (e) {
    yield put(domainsActions.propagationStatus.failure());
  }
}

function* getConfiguredDomainStatus(action) {
  const domain = action.payload;
  yield put(authActions.tokens.jwt.update());
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `api/v2/clientarea/br/domains/domain-propagation/${domain}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    const propagationStatus = domainsAssembly.configuredDomainStatus(response);
    yield put(domainsActions.configuredStatusDomain.success());
    yield put(domainsActions.configuredStatusDomain.set(propagationStatus));
  } catch (e) {
    yield put(domainsActions.configuredStatusDomain.failure());
  } finally {
    yield put(domainsActions.configuredStatusDomain.loaded());
  }
}

function* setConfiguredDomain(action) {
  const id = action.payload;
  yield put(authActions.tokens.jwt.update());
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: 'api/v2/clientarea/br/domains/set-external-domain-configured-flag',
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
    data: {
      hosting_id: id,
      configured: true,
    },
  };

  try {
    yield call(api.whmcs.post, request);
    yield put(domainsActions.setDomainConfiguration.success());
  } catch (e) {
    yield put(domainsActions.setDomainConfiguration.failure());
  }
}

function* setAllActiveDomainsList(action) {
  const setModalStatus = action.payload;
  yield put(authActions.tokens.jwt.update());
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: USER_DOMAINS_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  setModalStatus(ORDER_EPP_KEY_MODAL_STEP.LOADING_KEY);

  try {
    const response = yield call(api.whmcs.get, request);
    const activeDomainList = domainsAssembly.allActiveDomainsList(response);
    yield put(domainsActions.activeDomainList.set(activeDomainList));
    setModalStatus(ORDER_EPP_KEY_MODAL_STEP.SELECT_DOMAIN);
    yield put(domainsActions.activeDomainList.success());
  } catch (e) {
    yield put(domainsActions.activeDomainList.failure());
    setModalStatus(ORDER_EPP_KEY_MODAL_STEP.KEY_FAILURE);
  }
}

function* setGeneratedEppKey(action) {
  const { domainId, setModalStatus } = action.payload;
  yield put(authActions.tokens.jwt.update());
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `api/v2/clientarea/br/domain/${domainId}/eppcode`,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  setModalStatus(ORDER_EPP_KEY_MODAL_STEP.LOADING_KEY);

  try {
    const response = yield call(api.whmcs.get, request);
    const keyGenerated = domainsAssembly.eppKeyGenerated(response);
    yield put(domainsActions.modalEppKey.set(keyGenerated));
    setModalStatus(ORDER_EPP_KEY_MODAL_STEP.KEY_SUCCESS);
    yield put(domainsActions.modalEppKey.success());
  } catch (e) {
    setModalStatus(ORDER_EPP_KEY_MODAL_STEP.KEY_FAILURE);
    yield put(domainsActions.setDomainConfiguration.failure());
  }
}

function* requestDomains(action) {
  const { payload } = action;
  yield put(authActions.tokens.jwt.update());
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `${(payload && payload.page) ? `${USER_DOMAINS_URL}?page=${payload.page}` : USER_DOMAINS_URL}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    const domains = domainsAssembly.mountDomainsList(response);
    yield put(domainsActions.domains.set(payload ? { ...domains, page: payload && payload.page } : domains));
    yield put(domainsActions.domains.success());
  } catch (e) {
    const error = { ...e, userRequestData: request };
    logger.error('Error sagas requestDomains', error);
    yield put(domainsActions.domains.failure());
  }
}

function* requestDomainDetail(action) {
  const { id } = action.payload;
  yield put(authActions.tokens.jwt.update());
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: DOMAIN_DETAIL_URL(id),
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    const details = domainsAssembly.mountDetails(response);
    yield put(domainsActions.domains.setDetail(details));
    yield put(domainsActions.domains.detailSuccess());
  } catch (e) {
    yield put(domainsActions.domains.detailFailure());
  }
}

function* requestDomainsElegible() {
  yield put(authActions.tokens.jwt.update());
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `${USER_DOMAINS_ELEGIBLE}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    yield put(domainsActions.domainsElegible.set(response.data));
    yield put(domainsActions.domainsElegible.success());
  } catch (e) {
    yield put(domainsActions.domainsElegible.failure());
  }
}

function* requestChangeDomain(action) {
  const {
    domain,
    hostingId,
    isAddonDomain,
    awaitForAppointment,
    domainId,
    isOnboarding,
    oldDomain,
    fallback,
  } = action.payload;

  yield put(authActions.tokens.jwt.update());
  const { token } = yield select(state => state.auth);

  const request = {
    url: `${CHANGE_DOMAIN_URL}/${hostingId}/${isAddonDomain ? 'change-addon-domain' : 'change-primary-domain'}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    data: {
      new_domain: domain,
    },
  };

  if (isAddonDomain) {
    request.data.old_domain = oldDomain;
  }

  yield put(domainsActions.changeDomain.loading(true));
  try {
    yield call(api.whmcs.patch, request);

    if (!awaitForAppointment) {
      yield put(domainsActions.changeDomain.success());
      yield put(domainsActions.changeDomain.setNewDomain(domain));
    }

    if (awaitForAppointment) {
      yield put(sitesActions.changeDomainDns.request(domainId, hostingId, domain));
      const options = [
        SitesActionTypes.CHANGE_DOMAIN_DNS_SUCCESS,
        SitesActionTypes.CHANGE_DOMAIN_DNS_FAILURE,
      ];

      const action = yield take(options);

      if (options.includes(action.type)) {
        yield put(domainsActions.changeDomain.success());
        yield put(domainsActions.changeDomain.setNewDomain(domain));
      }
    }

    if (isOnboarding) {
      yield put(domainsActions.changeDomain.setNewDomainOnboardingSummary({ hostingId, newDomain: domain }));
    }

    yield put(domainsActions.changeDomain.loading(false));
    fallback && fallback();
  } catch (e) {
    yield put(domainsActions.changeDomain.loading(false));
    logger.error('Error requestChangeDomain', e);
    const errorCode = e && e.response && e.response.data && e.response.data.data && e.response.data.data.code;

    yield put(domainsActions.changeDomain.failure(errorCode === CHANGE_DOMAIN_CODES.NOT_UPDATE_DOMAIN_IN_CPANEL_HOSTING && CHANGE_DOMAIN_CODES.NOT_UPDATE_DOMAIN_IN_CPANEL_HOSTING));

    if (errorCode === CHANGE_DOMAIN_CODES.HOSTING_NOT_AVAILABLE) {
      yield put(
        commonActions.notifications.set({
          toast: {
            showIcon: true,
            title: locale('changeDomain.steps.inputDomain.backendErrors.HOSTING_NOT_AVAILABLE.title'),
            info: locale('changeDomain.steps.inputDomain.backendErrors.HOSTING_NOT_AVAILABLE.info'),
            context: 'error',
          },
          maxWidth: '460px',
        }),
      );

      return;
    }

    if (errorCode === CHANGE_DOMAIN_CODES.NOT_UPDATE_DOMAIN_IN_CPANEL_HOSTING) {
      yield put(
        commonActions.notifications.set({
          toast: {
            showIcon: true,
            title: locale('changeDomain.steps.inputDomain.backendErrors.NOT_UPDATE_DOMAIN_IN_CPANEL_HOSTING.title'),
            info: locale('changeDomain.steps.inputDomain.backendErrors.NOT_UPDATE_DOMAIN_IN_CPANEL_HOSTING.info'),
            context: 'error',
          },
          maxWidth: '460px',
        }),
      );

      return;
    }

    if (errorCode === CHANGE_DOMAIN_CODES.SAME_DOMAIN) {
      yield put(
        commonActions.notifications.set({
          toast: {
            showIcon: true,
            title: locale('changeDomain.steps.inputDomain.backendErrors.SAME_DOMAIN.title'),
            info: locale('changeDomain.steps.inputDomain.backendErrors.SAME_DOMAIN.info'),
            context: 'error',
          },
          maxWidth: '460px',
        }),
      );

      return;
    }

    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('changeDomain.steps.inputDomain.backendErrors.genericError.title'),
          info: locale('changeDomain.steps.inputDomain.backendErrors.genericError.info'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );
  }
}

function* requestTemporaryDomains() {
  yield put(authActions.tokens.jwt.update());
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  const request = {
    url: `${TEMPORARY_DOMAINS_URL}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  yield put(domainsActions.temporaryDomains.loading(true));
  try {
    const response = yield call(api.whmcs.get, request);
    yield put(domainsActions.temporaryDomains.set(response.data.data));
    yield put(domainsActions.temporaryDomains.success());
    yield put(domainsActions.temporaryDomains.loading(false));
  } catch (e) {
    yield put(domainsActions.temporaryDomains.failure());
    yield put(domainsActions.temporaryDomains.loading(false));
  }
}

export function* createTempDomain({ payload }) {
  const { hostingId } = payload;
  const { token, jwt: jwtToken } = yield select(state => state.auth);

  yield put(domainsActions.temporaryDomains.creation.loading(true));
  try {
    const request = {
      headers: {
        Authorization: `Bearer ${token}`,
        'X-Service-Token': jwtToken,
      },
      url: GENERATE_TEMP_DOMAIN(hostingId),
    };

    const response = yield call(api.whmcs.get, request);
    yield put(domainsActions.temporaryDomains.creation.set(response.data.data));
    yield put(domainsActions.temporaryDomains.creation.success());
    yield put(domainsActions.temporaryDomains.creation.loading(false));
    return response.data.data;
  } catch (e) {
    logger.error('Error createTempDomain', e);
    yield put(domainsActions.temporaryDomains.creation.failure());
    yield put(domainsActions.temporaryDomains.creation.loading(false));
  }
}

function* requestRedeemDomain(action) {
  const { token } = yield select(state => state.auth);
  const { redeemDomainsList, callbackFunc } = action.payload;

  const request = {
    url: PRODUCT_REDEEM_FAST_CHECKOUT_URL,
    data: {
      domains: [
        ...redeemDomainsList,
      ],
      offer_contexts: ['free_domain_redeem'],
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  yield put(domainsActions.redeemDomain.loading(true));

  try {
    yield call(api.whmcs.post, request);
    callbackFunc && callbackFunc();
    yield put(domainsActions.redeemDomain.success());

    yield put(domainsActions.redeemDomain.loading(false));
  } catch (e) {
    yield put(domainsActions.redeemDomain.failure());
    yield put(domainsActions.redeemDomain.loading(false));
  }
}

function* requestAvailableDomainTlds(action) {
  const { productId } = action.payload;
  const { token } = yield select(state => state.auth);

  const request = {
    url: `api/v2/clientarea/br/hostings/product/${productId}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    yield put(domainsActions.domainTlds.loading(true));
    const response = yield call(api.whmcs.get, request);
    yield put(domainsActions.domainTlds.set({ tldsResponse: response.data.data }));
    yield put(domainsActions.domainTlds.success());

    yield put(domainsActions.domainTlds.loading(false));
  } catch (e) {
    yield put(domainsActions.domainTlds.failure());
    yield put(domainsActions.domainTlds.loading(false));
  }
}

function* requestDomainAvailability(action) {
  const {
    sld, tld, isRadix,
  } = action.payload;
  const { token } = yield select(state => state.auth);

  const request = {
    url: `api/v2/clientarea/br/domains/check-availability/${tld}/${sld}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    yield put(domainsActions.domainAvailability.loading(true));

    const response = yield call(api.whmcs.get, request);

    const isAvailable = response.data.available;
    const currentDomain = `${sld}.${tld}`;
    const tldsList = response.data.domains;

    yield put(domainsActions.domainAvailability.set({
      isAvailable, currentDomain, tldsList, isRadix,
    }));
    yield put(domainsActions.domainAvailability.success());

    yield put(domainsActions.domainAvailability.loading(false));
  } catch (e) {
    yield put(domainsActions.domainAvailability.failure());
    yield put(domainsActions.domainAvailability.loading(false));
  }
}

function* requestCheckDocument(action) {
  const { numericDocument } = action.payload;
  const { token } = yield select(state => state.auth);

  const request = {
    url: `api/v3/registrobr/check-provider/${numericDocument}`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    yield put(domainsActions.checkProvider.loading(true));

    const response = yield call(api.whmcs.get, request);
    const available = response.data && response.data.data && response.data.data.available;

    yield put(domainsActions.checkProvider.set({ available }));
    yield put(domainsActions.checkProvider.success());

    yield put(domainsActions.checkProvider.loading(false));
  } catch (e) {
    yield put(domainsActions.checkProvider.failure());
    yield put(domainsActions.checkProvider.loading(false));
  }
}

function* getRedeemAvailability() {
  yield put(authActions.tokens.jwt.update());
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const request = {
    url: `${DOMAIN_REDEEM_AVAILABILITY}`,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
  };

  try {
    const response = yield call(api.whmcs.get, request);
    yield put(domainsActions.redeemAvailability.set(response.data.data.redeemAvailability));
    yield put(domainsActions.redeemAvailability.success());
  } catch (e) {
    yield put(domainsActions.redeemAvailability.failure());
  }
}

function* searchDomainToBuy(action) {
  const { tld, sld } = action.payload;
  let orderBy = '.com,.com.br,.online,.store,.shop,.site,.org,.net,.net.br,.tech,.info,.info.br,.app.br,.adv.br,.xyz,.fun,.website,.host,.top,.press,.space';
  let requiredTlds = '.com,.com.br,.online,.store,.shop,.site,.org,.net,.net.br,.tech,.info,.info.br,.app.br,.adv.br,.xyz,.fun,.website,.host,.top,.press,.space';

  if (!isBRServer) {
    orderBy = '.com,.net,.online,.store,.org,.space,.shop,.site,.info,.biz,.website,.xyz,.tech,.me,.in,.fun,.club,.host,.cc,.digital,.agency,.tv,.plus,.marketing,.press,.network,.life,.cl,.mx,.co,.com.co,.com.mx';
    requiredTlds = '.com,.net,.online,.store,.org,.space,.shop,.site,.info,.biz,.website,.xyz,.tech,.me,.in,.fun,.club,.host,.cc,.digital,.agency,.tv,.plus,.marketing,.press,.network,.life,.cl,.mx,.co,.com.co,.com.mx';
  }

  const request = {
    url: `${DOMAIN_PURCHASE_AVAILABILITY}/${tld}/${sld}?radix=1&orderBy=${orderBy}&requiredTlds=${requiredTlds}`,
  };

  yield put(domainsActions.buyDomain.search.loading(true));
  try {
    const response = yield call(api.whmcs.get, request);
    const putSearchedDomainToFirstPosition = (data) => {
      const searchedDomain = [...data.domains].find(item => item.domain === `${sld}.${tld}`);
      const filteredList = [...data.domains].filter(item => item.domain !== `${sld}.${tld}`);

      if (searchedDomain) {
        return {
          ...data,
          domains: [searchedDomain, ...filteredList],
        };
      }

      return {
        ...data,
      };
    };

    const sortedList = putSearchedDomainToFirstPosition(response.data);
    yield put(domainsActions.buyDomain.search.success({ result: sortedList }));
    yield put(domainsActions.buyDomain.search.loading(false));
  } catch (e) {
    yield put(domainsActions.buyDomain.search.success({ result: [] }));
    yield put(domainsActions.buyDomain.search.failure());
    yield put(domainsActions.buyDomain.search.loading(false));
    yield put(
      commonActions.notifications.set({
        toast: {
          showIcon: true,
          title: locale('buyDomain.search.error.title'),
          info: locale('buyDomain.search.error.description'),
          context: 'error',
        },
        maxWidth: '460px',
      }),
    );
  }
}

function* requestBuyDomain(action) {
  const { token, jwt: jwtToken } = yield select(state => state.auth);
  const { domains, redirectToInvoice } = action.payload;

  const request = {
    url: PRODUCT_REDEEM_FAST_CHECKOUT_URL,
    headers: {
      Authorization: `Bearer ${token}`,
      'X-Service-Token': jwtToken,
    },
    data: {
      domains,
      offer_contexts: ['domain_portal_sale'],
    },
  };

  yield put(domainsActions.buyDomain.loadingBuyDomain(true));
  try {
    const response = yield call(api.whmcs.post, request);
    const invoiceId = response.data.data.invoice_id;
    redirectToInvoice && redirectToInvoice(invoiceId);
    yield put(domainsActions.buyDomain.loadingBuyDomain(false));
  } catch (e) {
    logger.error('Error requestBuyDomain', e);
    yield put(domainsActions.buyDomain.loadingBuyDomain(false));
  }
}

function* sagas() {
  yield takeLatest(DomainsActionTypes.DOMAIN_ACTIVATE_RENEWAL_REQUEST, requestActivatingRenewal);
  yield takeLatest(DomainsActionTypes.DOMAIN_GENERATE_NEW_INVOICE_REQUEST, requestGenerationNewInvoice);
  yield takeLatest(DomainsActionTypes.GET_DOMAIN_PROPAGATION_STATUS_REQUEST, getPropagationStatus);
  yield takeLatest(DomainsActionTypes.GET_CONFIGURED_DOMAIN_STATUS_REQUEST, getConfiguredDomainStatus);
  yield takeLatest(DomainsActionTypes.SET_DOMAIN_HAS_CONFIGURED, setConfiguredDomain);
  yield takeLatest(DomainsActionTypes.GET_ALL_DOMAINS_LIST_REQUEST, setAllActiveDomainsList);
  yield takeLatest(DomainsActionTypes.GET_EPP_KEY_GENERATED_REQUEST, setGeneratedEppKey);
  yield takeLatest(DomainsActionTypes.GET_DOMAINS_REQUEST, requestDomains);
  yield takeLatest(DomainsActionTypes.GET_DOMAIN_DETAIL_REQUEST, requestDomainDetail);
  yield takeLatest(DomainsActionTypes.REQUEST_DOMAINS_ELEGIBLE, requestDomainsElegible);
  yield takeLatest(DomainsActionTypes.REQUEST_CHANGE_DOMAIN, requestChangeDomain);
  yield takeLatest(DomainsActionTypes.GET_DOMAINS_REDEEM_AVAILABILITY, getRedeemAvailability);
  yield takeLatest(DomainsActionTypes.GET_TEMPORARY_DOMAINS, requestTemporaryDomains);
  yield takeLatest(DomainsActionTypes.GET_CREATE_TEMPORARY_DOMAIN, createTempDomain);
  yield takeLatest(DomainsActionTypes.REQUEST_REDEEM_DOMAIN, requestRedeemDomain);
  yield takeLatest(DomainsActionTypes.GET_AVAILABLE_DOMAIN_TLDS, requestAvailableDomainTlds);
  yield takeLatest(DomainsActionTypes.REQUEST_DOMAIN_AVAILABILITY, requestDomainAvailability);
  yield takeLatest(DomainsActionTypes.REQUEST_CHECK_PROVIDER, requestCheckDocument);
  yield takeLatest(DomainsActionTypes.GET_DOMAINS_TO_BUY_REQUEST, searchDomainToBuy);
  yield takeLatest(DomainsActionTypes.REQUEST_BUY_DOMAIN, requestBuyDomain);
}

export default sagas;
